import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useBranches } from "../../hooks/branch";
import { ClientRoutes } from "../../routes";
import GlobalButton from "../CustomButton";
import Label from "../Label";
import TextInput from "../TextInputs";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
  "& .MuiPaper-root": {
    width: "500px",
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      margin: 12,
    },
  },
  "& .MuiDialogContent-root": { padding: "0 0 10px 0" },
}));

let paramsData = {};

const AddEditRollDialog = forwardRef(({ handleCreate, setIsLoading }, ref) => {
  const [open, setOpen] = useState(false);
  const [roleName, setRoleName] = useState("");
  const { user } = useSelector((state) => state.user);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const { allBranches, fetchBranches } = useBranches();

  useImperativeHandle(ref, () => ({
    isVisible(params) {
      paramsData = params;
      setOpen(true);
      if (params?.type === "edit") {
        setRoleName(params?.data?.role || "");
        setSelectedBranches(params?.data?.branches.map((b) => b._id) || []);

        setSelectedRoutes(params?.data?.pages || []);
      } else {
        setRoleName("");
        setSelectedBranches([]);
        setSelectedRoutes([]);
      }
    },
  }));

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleClose = () => {
    setOpen(false);
    setRoleName("");
    setSelectedBranches([]);
    setSelectedRoutes([]);
  };

  const handleToggleRoute = (route) => {
    setSelectedRoutes((prev) => {
      const isExist = prev.some((r) => r.path === route.path);
      let updatedRoutes = isExist
        ? prev.filter((r) => r.path !== route.path)
        : [...prev, { ...route, component: undefined }];
      updatedRoutes.sort((a, b) => a.id - b.id);

      return updatedRoutes;
    });
  };

  const handleCreateRole = () => {
    let params = {
      role: roleName,
      branches: selectedBranches,
      pages: selectedRoutes,
      businessId: user?.businessId,
      type: paramsData?.type,
      roleId: paramsData?.data?._id,
    };
    handleClose();
    handleCreate(params);
  };

  useEffect(() => {
    if (allBranches.length > 0) {
      setSelectedBranches((prevSelected) =>
        prevSelected.map(
          (id) => allBranches.find((b) => b._id === id)?._id || id
        )
      );
    }
  }, [allBranches]);
  useEffect(() => {
    if (paramsData?.type === "edit" && paramsData?.data?.pages) {
      const updatedRoutes = paramsData.data.pages
        .map((savedRoute) => {
          const route = ClientRoutes.find((r) => r.path === savedRoute.path);
          return route ? { ...route, component: undefined } : null;
        })
        .filter(Boolean);
      setSelectedRoutes(updatedRoutes);
    }
  }, [paramsData]);
  return (
    <BootstrapDialog onClose={handleClose} open={open} scroll="body">
      <Stack direction="row" justifyContent="space-between">
        <DialogTitle>
          {paramsData?.type == "edit" ? "Update Role" : "New Role"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <Label required labelText="Role Name" />
            <TextInput
              placeholder="Enter Role Name"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Label labelText="Select Branches" />
            <Select
              multiple
              fullWidth
              value={selectedBranches} // This should only contain _id values
              onChange={(e) => setSelectedBranches(e.target.value)}
              renderValue={(selected) =>
                selected
                  .map(
                    (id) =>
                      allBranches.find((b) => b._id === id)?.displayName ||
                      "Unknown"
                  )
                  .join(", ")
              }
            >
              {allBranches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  <Checkbox checked={selectedBranches.includes(branch._id)} />
                  <ListItemText primary={branch.displayName} />
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">All Modules</Typography>
            <List>
              {ClientRoutes.filter(
                (route) => route.name !== "Branches" && route.name !== "Setting"
              ).map((route) => {
                const isSelected = selectedRoutes.some(
                  (r) => r.path === route.path
                );
                return (
                  <ListItem
                    key={route.id}
                    button
                    onClick={() => handleToggleRoute(route)}
                  >
                    <ListItemIcon>
                      <Checkbox checked={isSelected} />
                    </ListItemIcon>
                    <ListItemText primary={route.name} />
                  </ListItem>
                );
              })}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <GlobalButton
          variant="outlined"
          btnText="Cancel"
          handleClickBtn={handleClose}
        />
        <GlobalButton
          variant="contained"
          btnText={paramsData?.type == "edit" ? "Update" : "Create"}
          handleClickBtn={handleCreateRole}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default AddEditRollDialog;
